import React, { useEffect, useRef } from 'react';
import { SecureTokenManager } from '../../utils/secureTokenManager';
import './GoogleSignIn.css';

declare global {
  interface Window {
    google: any;
  }
}

interface GoogleSignInProps {
  onSuccess: () => void;
  onFailure?: (error: any) => void;
}

const GoogleSignIn: React.FC<GoogleSignInProps> = ({ onSuccess, onFailure }) => {
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const tokenClientRef = useRef<any>(null);
  const [staySignedIn, setStaySignedIn] = React.useState(true);
  const [userEmail, setUserEmail] = React.useState<string | null>(null);

  useEffect(() => {
    const initializeGoogleSignIn = () => {
      if (typeof window !== 'undefined') {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => {
          if (window.google && googleButtonRef.current) {
            // Clear any existing content
            googleButtonRef.current.innerHTML = '';
            
            // Get stored email for silent auth
            const storedEmail = SecureTokenManager.getUserEmail();
            if (storedEmail) {
              setUserEmail(storedEmail);
            }

            // Initialize OAuth2 token client for Sheets scope and user profile
            tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
              client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
              scope: 'https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
              hint: storedEmail || undefined,
              callback: async (tokenResponse: any) => {
                if (tokenResponse.access_token) {
                  // Fetch user info to get email
                  if (!userEmail && !storedEmail) {
                    try {
                      const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
                        headers: {
                          'Authorization': `Bearer ${tokenResponse.access_token}`
                        }
                      });
                      const userInfo = await response.json();
                      setUserEmail(userInfo.email);
                      SecureTokenManager.setToken(
                        tokenResponse.access_token,
                        tokenResponse.expires_in || 3600,
                        userInfo.email,
                        staySignedIn
                      );
                    } catch (error) {
                      // If user info fetch fails, still save the token
                      SecureTokenManager.setToken(
                        tokenResponse.access_token,
                        tokenResponse.expires_in || 3600,
                        undefined,
                        staySignedIn
                      );
                    }
                  } else {
                    // Update token with existing email
                    SecureTokenManager.setToken(
                      tokenResponse.access_token,
                      tokenResponse.expires_in || 3600,
                      userEmail || storedEmail || undefined,
                      staySignedIn
                    );
                  }
                  onSuccess();
                }
              },
            });

            // Create a custom button that triggers OAuth2 flow
            const button = document.createElement('button');
            button.innerHTML = `
              <svg style="margin-right: 8px;" width="18" height="18" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            `;
            button.style.cssText = `
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 100%;
              padding: 12px 24px;
              background: white;
              border: 1px solid #dadce0;
              border-radius: 6px;
              font-size: 16px;
              font-weight: 500;
              color: #3c4043;
              cursor: pointer;
              transition: all 0.2s ease;
            `;
            button.onmouseover = () => {
              button.style.background = '#f8f9fa';
              button.style.boxShadow = '0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)';
            };
            button.onmouseout = () => {
              button.style.background = 'white';
              button.style.boxShadow = 'none';
            };
            button.onclick = handleManualSignIn;
            
            googleButtonRef.current.appendChild(button);
          }
        };
        document.head.appendChild(script);
      }
    };

    initializeGoogleSignIn();
    
    // Cleanup function to prevent duplicate buttons
    const currentButtonRef = googleButtonRef.current;
    return () => {
      if (currentButtonRef) {
        currentButtonRef.innerHTML = '';
      }
    };
  }, [onSuccess, staySignedIn, userEmail]);

  // Function to perform silent authentication
  const performSilentAuth = () => {
    if (tokenClientRef.current) {
      tokenClientRef.current.requestAccessToken({ prompt: '' });
    }
  };

  // Check if we can do silent auth on mount
  useEffect(() => {
    const storedEmail = SecureTokenManager.getUserEmail();
    const shouldStaySignedIn = SecureTokenManager.getStaySignedIn();
    
    if (storedEmail && shouldStaySignedIn && tokenClientRef.current) {
      // Attempt silent authentication after a short delay
      setTimeout(() => {
        performSilentAuth();
      }, 500);
    }
  }, []);

  const handleManualSignIn = () => {
    if (tokenClientRef.current) {
      tokenClientRef.current.requestAccessToken();
    }
  };

  return (
    <div className="google-signin-container">
      <div className="signin-card">
        <h1 className="signin-title">Welcome to Mailercloud Onboarding</h1>
        <p className="signin-description">
          Sign in with your Google account to access the onboarding platform
        </p>
        
        <div className="signin-buttons">
          <div ref={googleButtonRef} className="google-signin-wrapper"></div>
        </div>
        
        <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px', color: '#5f6368' }}>
            <input
              type="checkbox"
              checked={staySignedIn}
              onChange={(e) => setStaySignedIn(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            Stay signed in for 24 hours
          </label>
        </div>
        
        <p className="signin-note">
          Make sure you have access to the Google Sheets containing the onboarding data
        </p>
      </div>
    </div>
  );
};

export default GoogleSignIn;